import React, { useState, useEffect, useRef } from 'react'
import { fetchUtils, useDataProvider } from 'react-admin'
import IconButton from '@material-ui/core/IconButton'
import Popover from '@material-ui/core/Popover'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import Divider from '@material-ui/core/Divider'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import DeleteIcon from '@material-ui/icons/Delete'
import { makeStyles } from '@material-ui/core/styles'
import { getCurrentSession } from '../../lib/session'

// Get API URL - use same pattern as dataProvider
// Webpack should replace process.env.REACT_APP_API_URL at build time
// Using a constant that webpack can statically analyze and replace
const API_URL = process.env.REACT_APP_API_URL || ''

const useStyles = makeStyles((theme) => ({
  popover: {
    padding: theme.spacing(2),
    minWidth: 300,
    maxWidth: 450,
    maxHeight: 500,
    overflow: 'auto',
  },
  tagTypeGroup: {
    marginBottom: theme.spacing(2),
  },
  tagTypeName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  tagChip: {
    margin: theme.spacing(0.25),
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  autocomplete: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  deleteIcon: {
    fontSize: '0.875rem',
    marginLeft: theme.spacing(0.5),
    cursor: 'pointer',
  },
}))

const TagIcon = ({ record }) => {
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const [anchorEl, setAnchorEl] = useState(null)
  const [tags, setTags] = useState([])
  const [allTags, setAllTags] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingTags, setLoadingTags] = useState(false)
  const [error, setError] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [modalityId, setModalityId] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const hasInitialFetch = useRef(false)
  const isUserInteracting = useRef(false)
  const leaveTimeoutRef = useRef(null)

  const open = Boolean(anchorEl)

  // Store the modality ID when record is available
  useEffect(() => {
    if (record?.id) {
      setModalityId(record.id)
    }
  }, [record?.id])

  const handleMouseEnter = (event) => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    setAnchorEl(event.currentTarget)
    isUserInteracting.current = false
    // Only fetch tags if we don't have them or if there are no unsaved changes
    if (!loading && !isFetching && !hasChanges) {
      hasInitialFetch.current = false
      fetchTags()
    }
    // Always fetch all tags if not already loaded
    if (allTags.length === 0) {
      fetchAllTags()
    }
  }

  const handleMouseLeave = () => {
    // Add a small delay to allow mouse to move to popover
    // This prevents the popover from closing when moving from icon to popover
    leaveTimeoutRef.current = setTimeout(() => {
      if (!hasChanges) {
        setAnchorEl(null)
        setSelectedTags([])
        setHasChanges(false)
        isUserInteracting.current = false
        hasInitialFetch.current = false
        // Clear tags so they're refetched on next hover
        setTags([])
      }
      leaveTimeoutRef.current = null
    }, 100) // 100ms delay to allow mouse to move to popover
  }

  const handlePopoverMouseEnter = () => {
    // Clear any pending leave timeout when mouse enters popover
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    // Keep the popover open
    if (anchorEl) {
      setAnchorEl(anchorEl)
    }
  }

  const handlePopoverMouseLeave = () => {
    // Close popover when mouse leaves the popover itself
    if (!hasChanges) {
      setAnchorEl(null)
      setSelectedTags([])
      setHasChanges(false)
      isUserInteracting.current = false
      hasInitialFetch.current = false
      setTags([])
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  const fetchTags = async () => {
    const id = modalityId || record?.id
    if (!id) {
      console.error('No modality ID available for fetching tags')
      return
    }

    // Prevent multiple simultaneous fetches
    if (isFetching) {
      return
    }

    setIsFetching(true)
    setLoading(true)
    setError(null)
    try {
      if (!API_URL) {
        throw new Error('API URL not configured')
      }

      const credentials = await getCurrentSession()
      const url = `${API_URL}/modalities/${id}/tags`
      
      const { json } = await fetchUtils.fetchJson(url, {
        headers: new Headers({
          'Authorization': `Bearer ${credentials.idToken.jwtToken}`,
          'Accept': 'application/json',
        }),
      })

      const fetchedTags = json.data || []
      setTags(fetchedTags)
      // Only update selectedTags on initial fetch or if user hasn't started interacting
      // This prevents overwriting user's current selections in the Autocomplete
      if (!hasInitialFetch.current && !isUserInteracting.current) {
        setSelectedTags(fetchedTags.map(t => t.tagId))
        hasInitialFetch.current = true
      }
    } catch (err) {
      console.error('Error fetching tags:', err)
      setError('Failed to load tags')
    } finally {
      setLoading(false)
      setIsFetching(false)
    }
  }

  const fetchAllTags = async () => {
    if (allTags.length > 0) return // Already loaded

    setLoadingTags(true)
    try {
      const { data } = await dataProvider.getList('tags', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'tagTypeName', order: 'ASC' },
      })
      setAllTags(data || [])
    } catch (err) {
      console.error('Error fetching all tags:', err)
    } finally {
      setLoadingTags(false)
    }
  }

  const handleTagChange = (event, newValue) => {
    isUserInteracting.current = true
    const newTagIds = newValue.map(tag => tag.id)
    setSelectedTags(newTagIds)
    // Compare against the original tags from the server, not the current tags state
    // Use a ref or compare against the last saved state
    const originalTagIds = tags.map(t => t.tagId).sort()
    const newTagIdsSorted = [...newTagIds].sort()
    setHasChanges(JSON.stringify(newTagIdsSorted) !== JSON.stringify(originalTagIds))
  }

  const handleRemoveTag = (tagId) => {
    const newTagIds = selectedTags.filter(id => id !== tagId)
    setSelectedTags(newTagIds)
    setHasChanges(true)
  }

  const handleSave = async (e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    
    const id = modalityId || record?.id
    if (!id) {
      console.error('No modality ID available for saving tags', { modalityId, record })
      setError('Unable to save: modality ID not available')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (!API_URL) {
        throw new Error('API URL not configured')
      }

      console.log('Saving tags for modality:', id, 'tagIds:', selectedTags)
      
      const credentials = await getCurrentSession()
      const url = `${API_URL}/modalities/${id}/tags`
      
      console.log('Making PUT request to:', url, 'with body:', { tagIds: selectedTags })
      
      const response = await fetchUtils.fetchJson(url, {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${credentials.idToken.jwtToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ tagIds: selectedTags }),
      })

      console.log('Save response:', response)

      const json = response.json || response
      const updatedTags = json.data || []
      
      console.log('Updated tags:', updatedTags)
      
      setTags(updatedTags)
      setSelectedTags(updatedTags.map(t => t.tagId))
      setHasChanges(false)
      isUserInteracting.current = false
      hasInitialFetch.current = true
    } catch (err) {
      console.error('Error saving tags:', err)
      console.error('Error details:', {
        message: err.message,
        body: err.body,
        status: err.status,
        statusText: err.statusText
      })
      const errorMessage = err.message || err.body?.message || 'Failed to save tags'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setSelectedTags(tags.map(t => t.tagId))
    setHasChanges(false)
    setAnchorEl(null)
  }

  // Get selected tag objects for display
  const getSelectedTagObjects = () => {
    return tags.filter(t => selectedTags.includes(t.tagId))
  }

  // Group tags by tag type
  const groupedTags = getSelectedTagObjects().reduce((acc, tag) => {
    const tagTypeName = tag.tagTypeName || 'Uncategorized'
    if (!acc[tagTypeName]) {
      acc[tagTypeName] = []
    }
    acc[tagTypeName].push(tag)
    return acc
  }, {})

  // Get selected tags for autocomplete
  const selectedTagObjects = allTags.filter(tag => selectedTags.includes(tag.id))

  return (
    <>
      <IconButton
        size="small"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="View tags"
        title="View tags"
      >
        <LocalOfferIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverMouseLeave}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
        style={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
          style: { pointerEvents: 'auto' },
        }}
      >
        <Box 
          className={classes.popover}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Typography variant="subtitle2" gutterBottom>
            Manage Tags
          </Typography>
          
          {loading && (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
            </Box>
          )}
          
          {error && (
            <Box className={classes.emptyState}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && (
            <>
              <Autocomplete
                multiple
                options={allTags}
                getOptionLabel={(option) => `${option.tagTypeName || 'Uncategorized'}: ${option.name}`}
                value={selectedTagObjects}
                onChange={handleTagChange}
                loading={loadingTags}
                size="small"
                className={classes.autocomplete}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Add tags..."
                    label="Tags"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={`${option.tagTypeName || 'Uncategorized'}: ${option.name}`}
                      size="small"
                    />
                  ))
                }
              />

              {Object.keys(groupedTags).length > 0 && (
                <>
                  <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    Current Tags:
                  </Typography>
                  {Object.keys(groupedTags).map((tagTypeName) => (
                    <Box key={tagTypeName} className={classes.tagTypeGroup}>
                      <Typography className={classes.tagTypeName}>
                        {tagTypeName}
                      </Typography>
                      <Box>
                        {groupedTags[tagTypeName].map((tag) => {
                          const tagTypeColor = tag.tagTypeColor || tag.tagType?.color
                          return (
                            <Chip
                              key={tag.id}
                              label={tag.tagName || tag.name}
                              size="small"
                              className={classes.tagChip}
                              onDelete={() => handleRemoveTag(tag.tagId)}
                              style={
                                tagTypeColor
                                  ? {
                                      backgroundColor: tagTypeColor,
                                      color: '#fff',
                                    }
                                  : {}
                              }
                            />
                          )
                        })}
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {Object.keys(groupedTags).length === 0 && !loadingTags && (
                <Box className={classes.emptyState}>
                  <Typography variant="body2">No tags assigned</Typography>
                </Box>
              )}

              {hasChanges && (
                <Box className={classes.actions}>
                  <Button
                    size="small"
                    onClick={handleCancel}
                    style={{ marginRight: 8 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      handleSave(e)
                    }}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default TagIcon

